import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const NAVY   = 'hsl(var(--c4p-navy-900))';
const CCY_LABELS = { EUR: 'Euro', USD: 'US Dollar', CAD: 'Canadian Dollar', AUD: 'Australian Dollar' };
const CCY_FLAGS  = { EUR: '🇪🇺', USD: '🇺🇸', CAD: '🇨🇦', AUD: '🇦🇺' };

function fmtDate(iso) {
  if (!iso) return 'Never';
  try {
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function FxRatesAdmin() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    setLoading(true);
    adminAPI.getFxRates()
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load FX rates'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data: d } = await adminAPI.refreshFxRates();
      setData(d);
      toast.success('FX rates refreshed from open.er-api.com');
    } catch {
      toast.error('Failed to refresh FX rates');
    } finally { setRefreshing(false); }
  };

  const isStale = data?.updated_at
    ? (Date.now() - new Date(data.updated_at).getTime()) > 24 * 60 * 60 * 1000
    : true;

  return (
    <div className="space-y-5 max-w-xl" data-testid="admin-fx-rates">
      <div>
        <h3 className="font-semibold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>
          FX Rates — GBP Base
        </h3>
        <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
          Used only for consolidating multi-currency reporting into GBP.
          Not used for quote pricing. Refreshed automatically every 24 hours.
        </p>
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3"
          style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted))' }}>
          <div className="flex items-center gap-2">
            {isStale
              ? <AlertCircle size={14} style={{ color: '#d97706' }} />
              : <CheckCircle2 size={14} style={{ color: '#16a34a' }} />}
            <span className="text-xs font-semibold" style={{ color: isStale ? '#d97706' : '#16a34a' }}>
              {isStale ? 'Rates may be outdated' : 'Up to date'}
            </span>
            <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
              &middot; Last updated: {loading ? '...' : fmtDate(data?.updated_at)}
            </span>
            {data?.source && (
              <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#f0f9ff', color: '#0369a1' }}>
                {data.source}
              </span>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={handleRefresh} disabled={refreshing}
            className="gap-1.5" data-testid="fx-refresh-btn">
            {refreshing ? <RefreshCw size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Refresh Now
          </Button>
        </div>

        {/* Rates table */}
        {loading ? (
          <div className="p-5 space-y-3">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : (
          <div>
            {/* GBP base row */}
            <div className="flex items-center justify-between px-5 py-3.5"
              style={{ borderBottom: '1px solid hsl(var(--border))', background: '#f0fdf4' }}>
              <div className="flex items-center gap-3">
                <span className="text-lg">🇬🇧</span>
                <div>
                  <p className="text-sm font-semibold" style={{ color: NAVY }}>British Pound</p>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>GBP — Base currency</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold tabular-nums" style={{ color: '#16a34a' }}>1.0000</p>
                <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Base</p>
              </div>
            </div>
            {Object.entries(data?.rates || {}).sort().map(([ccy, rate]) => (
              <div key={ccy} className="flex items-center justify-between px-5 py-3.5"
                style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                <div className="flex items-center gap-3">
                  <span className="text-lg">{CCY_FLAGS[ccy] || '🌐'}</span>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: NAVY }}>{CCY_LABELS[ccy] || ccy}</p>
                    <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{ccy}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold tabular-nums" style={{ color: NAVY }}>{Number(rate).toFixed(4)}</p>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>1 GBP</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="px-5 py-3 text-xs" style={{ borderTop: '1px solid hsl(var(--border))', color: 'hsl(var(--muted-fg))' }}>
          Rates sourced from open.er-api.com. To convert a value in foreign currency to GBP: divide by the rate above.
          Example: €117 EUR ÷ {data?.rates?.EUR?.toFixed(4) || '1.1586'} = £100 GBP.
        </div>
      </div>
    </div>
  );
}
